import * as LucideIcons from "lucide-react";
import { Target, Quote, type LucideIcon } from "lucide-react";
import { PageShell, PageHero } from "@/components/knmc/PageShell";
import type { AboutUsData } from "@/hooks/useAboutUs";

function resolveIcon(name?: string): LucideIcon {
  if (!name) return Target;
  const Icon = (LucideIcons as unknown as Record<string, LucideIcon>)[name];
  return Icon ?? Target;
}

function getInitials(fullName?: string): string {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function AboutPage({ aboutUs }: { aboutUs: AboutUsData }) {
  const data = aboutUs;

  return (
    <PageShell>
      <PageHero eyebrow="About KNMC" title={data.test} description={data.sub_heading} />

      {data.values.length > 0 && (
        <section className="bg-surface py-20">
          <div className="mx-auto max-w-7xl px-6">
            <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">Our values</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {data.values.map((v) => {
                const Icon = resolveIcon(v.icon);
                return (
                  <div key={v.key} className="rounded-2xl border border-border bg-card p-6">
                    <Icon className="h-8 w-8 text-secondary" />
                    <h3 className="mt-4 font-display font-space text-lg font-semibold text-foreground">
                      {v.key}
                    </h3>
                    <p className="mt-1.5 text-sm text-muted-foreground">{v.value}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {data.testimonials.length > 0 && (
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-14 max-w-2xl">
              <div className="mb-5 flex items-center gap-3">
                <span className="h-px w-10 bg-gold" />
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">In their words</p>
              </div>
              <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">
                {data.testimonials[0]?.title}
              </h2>
            </div>

            <div className="space-y-10">
              {data.testimonials.map((m, i) => {
                const reverse = i % 2 === 1;
                const initials = getInitials(m.from);
                return (
                  <article
                    key={`${m.from}-${i}`}
                    className="group relative overflow-hidden rounded-3xl border border-border bg-card p-8 shadow-soft transition-all duration-500 hover:border-gold/40 hover:shadow-elevated md:p-12"
                  >
                    <Quote
                      aria-hidden
                      className="pointer-events-none absolute right-6 top-6 h-20 w-20 text-gold/10 transition-transform duration-700 group-hover:scale-110 md:h-28 md:w-28"
                      strokeWidth={1}
                    />
                    <div
                      className={`relative flex flex-col items-center gap-10 md:flex-row ${
                        reverse ? "md:flex-row-reverse" : ""
                      }`}
                    >
                      <div className="shrink-0">
                        <div className="relative">
                          <div
                            aria-hidden
                            className="absolute -inset-2 rounded-full bg-linear-to-br from-gold/30 via-primary/20 to-secondary/30 opacity-60 blur-xl transition-opacity duration-500 group-hover:opacity-100"
                          />
                          {m.photo ? (
                            <img
                              src={m.photo}
                              alt={m.from}
                              className="block h-36 w-36 shrink-0 rounded-full border-4 border-card object-cover shadow-elevated ring-1 ring-gold/30 md:h-44 md:w-44"
                            />
                          ) : (
                            <div className="relative flex h-36 w-36 items-center justify-center rounded-full border-4 border-card bg-linear-to-br from-primary to-secondary font-display text-4xl font-semibold text-primary-foreground shadow-elevated ring-1 ring-gold/30 md:h-44 md:w-44 md:text-5xl">
                              {initials}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className="space-y-4 text-base leading-relaxed text-muted-foreground [&_h2]:font-display [&_h2]:font-space [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:leading-tight [&_h2]:text-primary [&_h2]:md:text-3xl [&_p]:mt-4 first:[&_p]:mt-0"
                          dangerouslySetInnerHTML={{ __html: m.content }}
                        />
                        <span
                          aria-hidden
                          className="mt-4 block h-px w-12 bg-gold/60 transition-all duration-500 group-hover:w-24"
                        />
                        <div className="mt-6">
                          <p className="font-display text-base font-semibold text-foreground">
                            {m.from}
                          </p>
                          <p className="text-sm italic text-muted-foreground">{m.position}</p>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {data.stats.length > 0 && (
        <section className="bg-primary py-20 text-primary-foreground">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 md:grid-cols-4">
            {data.stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-5xl font-semibold text-gold">{s.number}</div>
                <div className="mt-2 text-primary-foreground/80">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </PageShell>
  );
}